import React, { useState, useEffect } from 'react';
import { ExternalLink, Download, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { dataUrlToBlob, downloadFile, openFileInNewTab } from '../utils/fileViewer';

export default function DocumentViewerModal({
  open,
  onOpenChange,
  fileUrl,
  fileName = 'Documento',
  title = 'Visualização de Documento',
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(true);
  const [isImage, setIsImage] = useState(false);

  useEffect(() => {
    if (!open || !fileUrl) {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
      setBlobUrl(null);
      return;
    }

    const lowerName = (fileName || '').toLowerCase();
    const isImg = lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.webp') || fileUrl.startsWith('data:image/');
    setIsImage(isImg);
    setIsPdf(!isImg);

    if (fileUrl.startsWith('data:')) {
      const blob = dataUrlToBlob(fileUrl);
      if (blob) {
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } else {
        setBlobUrl(fileUrl);
      }
    } else {
      setBlobUrl(fileUrl);
    }

    return () => {
      // Clean up object URL when component unmounts or changes
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, fileUrl, fileName]);

  const handleDownload = () => {
    downloadFile(fileUrl, fileName);
  };

  const handleOpenNewTab = () => {
    openFileInNewTab(fileUrl, fileName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-2xl">
        <DialogHeader className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex flex-row items-center justify-between gap-4 space-y-0 bg-neutral-50 dark:bg-neutral-900/50">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-950/60 text-brand flex items-center justify-center shrink-0">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate flex items-center gap-2">
                <span className="truncate">{title || fileName}</span>
                <Badge variant="outline" className="text-[10px] uppercase font-semibold shrink-0">
                  {isImage ? 'Imagem' : 'PDF'}
                </Badge>
              </DialogTitle>
              {fileName && title !== fileName && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                  {fileName}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenNewTab}
              className="text-xs gap-1.5 h-8"
              title="Abrir em uma nova aba do navegador"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Nova Guia</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="text-xs gap-1.5 h-8 bg-brand/5 hover:bg-brand/10 text-brand border-brand/20"
              title="Baixar arquivo para o computador"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Baixar</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="p-3 bg-neutral-100 dark:bg-neutral-950">
          {blobUrl ? (
            isImage ? (
              <div className="flex items-center justify-center min-h-[60vh] max-h-[78vh] overflow-auto bg-neutral-900/10 dark:bg-neutral-900/50 rounded-lg p-2">
                <img
                  src={blobUrl}
                  alt={fileName}
                  className="max-h-[75vh] max-w-full object-contain rounded shadow-sm"
                />
              </div>
            ) : (
              <iframe
                src={`${blobUrl}#toolbar=1`}
                title={fileName}
                className="w-full h-[76vh] border-0 rounded-lg shadow-sm bg-neutral-800"
              />
            )
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-neutral-500 gap-2">
              <FileText className="h-8 w-8 text-neutral-400 animate-pulse" />
              <p className="text-xs">Carregando visualização do documento...</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
